import _ from 'underscore';
import { getUrlProtocolAndDomain } from '@bingads-webui/url-util';
import { getCharCountExcludingAdCustomizers } from '@bingads-webui-campaign/ad-customizers-next';
import { validateAdUrl } from '@bingads-webui-campaign-react/url-input-fluent';

const textFieldErrorMessage = _TL_('This field is required');
const lengthLimitErrorMessage = _TL_('The number of characters in this field cannot exceed {{lengthLimit}}');
const requirePath1ErrorMessage = _TL_('In order to use path 2, path 1 must also be used.');
const pathLengthLimitErrorMessages = [
  _TL_('Path 1 cannot exceed 15 characters.'),
  _TL_('Path 2 cannot exceed 15 characters.'),
];

export const URL_COLON_SLASH_SLASH = '://';
export const DEFAULT_URL_PREFIX = 'https';
export const IS_DISTINGUISH_WIDE_CHAR = true;
export const AD_TYPE_RSA = 'ResponsiveSearchAd';

export const adsFields = {
  DESTINATION_URL: 'FinalUrls',
  DISPLAY_URL: 'DisplayUrl',
  PATH1: 'Path1',
  PATH2: 'Path2',
  MOBILE_URL: 'MobileFinalUrls',
  HEADLINES: 'Headlines',
  DESCRIPTIONS: 'Descriptions',
  TRACKING_URL_TEMPLATE: 'TrackingUrlTemplate',
  FINAL_URL_SUFFIX: 'FinalUrlSuffix',
  URL_CUSTOM_PARAMETERS: 'UrlCustomParameters',
};

export const adsFieldAlias = {
  [adsFields.HEADLINES]: 'AdHeadlines',
  [adsFields.DESCRIPTIONS]: 'AdDescriptions',
};

export const adsFieldLengthLimitMap = {
  FinalUrls: 2048,
  Path1: 15,
  Path2: 15,
  DisplayUrl: 2048,
  MobileFinalUrls: 2048,
  Headlines: 30,
  LongHeadlines: 90,
  Descriptions: 90,
  TrackingUrlTemplate: 2048,
  FinalUrlSuffix: 1024,
  UrlCustomParametersKey: 16,
  UrlCustomParametersValue: 2048,
};

// This was used to bypass the requirement to use the actual i18n object
// in scenarios where we only want to determine if a given field passes
// validation - i.e., we don't care about the actual error message itself.
export const notI18n = {
  getString: _.identity,
};

/**
 * Calculate the max length for the destination URL without prefix.
 * @param {String} urlPrefix - URL prefix.
 * @returns {Number} - Max length for the destination URL without prefix.
 */
export function getDestinationUrlWithoutPrefixLengthLimit(urlPrefix) {
  const key = adsFields.DESTINATION_URL;

  return adsFieldLengthLimitMap[key] - urlPrefix.length - URL_COLON_SLASH_SLASH.length;
}

/**
 * Validates a text field value.
 * @param {String} value - Value of the text field.
 * @param {Number} lengthLimit - Max length allowed for the value.
 * @param {Object} [i18n] - i18n library.
 * @param {String} field - field.
 * @param {Boolean} [optional=false] - If true, value is allowed to be empty.
 * @param {String} [adType] - ResponsiveSearchAd or ResponsiveAd.
 * @returns {String} - Null if value is valid. Error message if invalid.
 */
export function validateTextFieldValue(
  value, lengthLimit, i18n = notI18n, field,
  optional = false, adType = AD_TYPE_RSA
) {
  const size = getCharCountExcludingAdCustomizers({
    text: value,
    adType,
    field,
  });

  if (!optional && size === 0) {
    return i18n.getString(textFieldErrorMessage);
  }

  if (size > lengthLimit) {
    // character count exceeds limit
    return i18n.getString(lengthLimitErrorMessage, { lengthLimit });
  }

  return null;
}

export function validateTextField(fieldName, i18n, optional, adType = AD_TYPE_RSA) {
  return (value) => {
    const lengthLimit = adsFieldLengthLimitMap[fieldName];
    const errMsg = validateTextFieldValue(value, lengthLimit, i18n, fieldName, optional, adType);
    return errMsg || '';
  };
}

export function validateDestinationUrl(urlPrefix, campaignWebsite, i18n, optional = false) {
  const campaignUrlProtocolAndDomain = getUrlProtocolAndDomain(campaignWebsite);

  return (value) => {
    if (optional && _.isEmpty(value)) {
      return '';
    }

    const selectedUrlPrefix = urlPrefix || DEFAULT_URL_PREFIX;
    const url = `${selectedUrlPrefix}${URL_COLON_SLASH_SLASH}${value}`;

    const errMsg = validateAdUrl(url, campaignUrlProtocolAndDomain, i18n);

    return errMsg || '';
  };
}

export function validateTextAssets(assets, config, i18n) {
  let error;
  const { minSize, errorMap } = config;

  const availableAssets = _.filter(assets, a => !_.isEmpty(a.Asset.Text.trim()))
    .map(a => ({
      Text: a.Asset.Text.trim(),
      PinnedField: a.PinnedField,
    }));

  if (_.size(availableAssets) < minSize) {
    error = errorMap.lessThanRequired;
  } else if (_.size(_.uniq(availableAssets, 'Text')) < _.size(availableAssets)) {
    error = errorMap.duplicate;
  } else {
    const unpinnedSize = _.size(_.filter(availableAssets, a => a.PinnedField === 'None'));
    if (unpinnedSize < minSize) {
      const pinnedSize = _.size(_.uniq(availableAssets, 'PinnedField')) - (unpinnedSize ? 1 : 0);
      if (pinnedSize < minSize - unpinnedSize) {
        error = errorMap.lessPosition;
      }
    }
  }

  return error ? i18n.getString(error) : '';
}

export function validatePath(values, i18n) {
  const errors = [];

  if (_.isEmpty(values[0]) && !_.isEmpty(values[1])) {
    errors.push({
      Property: adsFields.PATH1,
      Message: i18n.getString(requirePath1ErrorMessage),
    });
  }

  _.each([
    adsFields.PATH1,
    adsFields.PATH2,
  ], (field, index) => {
    const error =
      validateTextFieldValue(values[index], adsFieldLengthLimitMap[field], i18n, field, true);
    if (error) {
      errors.push({
        Property: field,
        Message: i18n.getString(pathLengthLimitErrorMessages[index]),
      });
    }
  });

  return errors;
}
